/* =========================================================
   Mapa de campos: nombre en la aplicación → columna en Postgres.

   Es la única traducción entre los dos mundos. El frontend, las
   plantillas de Excel y los esquemas de campo usan PascalCase; la
   base de datos usa snake_case. Todo lo que lea o escriba la API
   pasa por aquí, así que si un campo cambia, cambia en un solo sitio.

   `kind` dice cómo convertir el valor:
     text     cadena, '' se guarda como ''  (NULL = "nunca se capturó")
     int      entero, vacío o no numérico → NULL
     date     'yyyy-mm-dd', vacío → NULL
     bool     booleano
     country  código ISO con clave foránea a countries; '' → NULL, porque
              la cadena vacía no es un país y rompería la restricción
   ========================================================= */

export type ColumnKind = 'text' | 'int' | 'date' | 'bool' | 'country' | 'list';

export interface ColumnMap {
  [appField: string]: { col: string; kind: ColumnKind };
}

const t = (col: string) => ({ col, kind: 'text' as const });
const i = (col: string) => ({ col, kind: 'int' as const });
const d = (col: string) => ({ col, kind: 'date' as const });
const b = (col: string) => ({ col, kind: 'bool' as const });
const c = (col: string) => ({ col, kind: 'country' as const });
/** Columna `text[]`: industria, portafolio, entorno de formación. */
const l = (col: string) => ({ col, kind: 'list' as const });

/** El separador de las listas cuando viajan como texto: en una celda de
 *  Excel, en la plantilla de importación y en el informe exportado.
 *
 *  Punto y coma y no coma, porque varias etiquetas la llevan dentro
 *  —«Arquitectura, ingeniería y construcción»— y partir por coma las
 *  rompería en tres valores inexistentes. */
export const SEP_LISTA = ';';

/** Convierte lo que llega —lista ya hecha, o texto con separadores— en una
 *  lista de códigos sin repetidos ni huecos. */
export function aLista(raw: unknown): string[] {
  const partes = Array.isArray(raw)
    ? raw.map((v) => String(v))
    : String(raw ?? '').split(SEP_LISTA);
  return [...new Set(partes.map((s) => s.trim()).filter(Boolean))];
}

export const ORG_COLUMNS: ColumnMap = {
  OrgId: t('org_id'),
  OrgName: t('org_name'),
  OrgCountry: c('org_country'),
  OrgWebAddress: t('org_web_address'),

  ContractAddress1: t('contract_address1'),
  ContractAddress2: t('contract_address2'),
  ContractAddress3: t('contract_address3'),
  ContractCity: t('contract_city'),
  ContractCountry: c('contract_country'),
  ContractPostalCode: t('contract_postal_code'),
  ContractStateProvince: t('contract_state_province'),

  RegisteredAddress1: t('registered_address1'),
  RegisteredAddress2: t('registered_address2'),
  RegisteredAddress3: t('registered_address3'),
  RegisteredCity: t('registered_city'),
  RegisteredPostalCode: t('registered_postal_code'),
  RegisteredStateProvince: t('registered_state_province'),

  InvoicingAddress1: t('invoicing_address1'),
  InvoicingAddress2: t('invoicing_address2'),
  InvoicingAddress3: t('invoicing_address3'),
  InvoicingCity: t('invoicing_city'),
  InvoicingCountry: c('invoicing_country'),
  InvoicingPostalCode: t('invoicing_postal_code'),
  InvoicingStateProvince: t('invoicing_state_province'),

  LegalContactFirstName: t('legal_contact_first_name'),
  LegalContactLastName: t('legal_contact_last_name'),
  LegalContactEmailAddress: t('legal_contact_email_address'),
  LegalContactTelephone: t('legal_contact_telephone'),
  LegalContactFax: t('legal_contact_fax'),

  BillingContactFirstName: t('billing_contact_first_name'),
  BillingContactLastName: t('billing_contact_last_name'),
  BillingContactEmailAddress: t('billing_contact_email_address'),
  BillingContactTelephone: t('billing_contact_telephone'),
  BillingContactFax: t('billing_contact_fax'),

  YearJoined: i('year_joined'),
  PrimarySiteId: t('primary_site_id'),
  LastUpdated: d('last_updated'),

  // Perfil del socio. No viene del padrón de Autodesk: es lo que el proceso
  // de actualización existe para recoger.
  EmployeeCount: t('employee_count'),
  OrgIndustry: l('org_industry'),
  ProductPortfolio: l('product_portfolio'),
  ProductPortfolioOther: t('product_portfolio_other'),
  TrainingEnvironment: l('training_environment'),
  OffersCertPrep: t('offers_cert_prep'),
  OffersEduTraining: t('offers_edu_training'),
  InterestedEduTraining: t('interested_edu_training'),
};

export const SITE_COLUMNS: ColumnMap = {
  SiteId: t('site_id'),
  OrgId: t('org_id'),

  SiteName: t('site_name'),
  CommercialSiteName: t('commercial_site_name'),
  ATCCSN: t('atccsn'),
  SiteCountry: c('site_country'),

  SiteAddress1: t('site_address1'),
  SiteAddress2: t('site_address2'),
  SiteAddress3: t('site_address3'),
  SiteCity: t('site_city'),
  SiteStateProvince: t('site_state_province'),
  SitePostalCode: t('site_postal_code'),
  RegisteredPostalCode: t('registered_postal_code'),
  RegisteredStateProvince: t('registered_state_province'),

  SiteTelephone: t('site_telephone'),
  SiteFax: t('site_fax'),
  SiteEmailAddress: t('site_email_address'),
  SiteWebAddress: t('site_web_address'),

  Workstations: i('workstations'),

  ATCDirectorFirstName: t('atc_director_first_name'),
  ATCDirectorLastName: t('atc_director_last_name'),
  ATCDirectorEmail: t('atc_director_email'),
  ATCDirectorTelephone: t('atc_director_telephone'),
  ATCDirectorFax: t('atc_director_fax'),

  SiteAdminFirstName: t('site_admin_first_name'),
  SiteAdminLastName: t('site_admin_last_name'),
  SiteAdminEmail: t('site_admin_email'),
  SiteAdminTelephone: t('site_admin_telephone'),
  SiteAdminFax: t('site_admin_fax'),

  SiteManagerFirstName: t('site_manager_first_name'),
  SiteManagerLastName: t('site_manager_last_name'),
  SiteManagerEmail: t('site_manager_email'),
  SiteManagerTelephone: t('site_manager_telephone'),
  SiteManagerFax: t('site_manager_fax'),

  YearJoined: i('year_joined'),
  LastUpdated: d('last_updated'),
};

export const INSTRUCTOR_COLUMNS: ColumnMap = {
  InstructorId: t('instructor_id'),
  EnglishFirstName: t('english_first_name'),
  EnglishLastName: t('english_last_name'),
  EmailAddress: t('email_address'),
  Mobile1: t('mobile1'),
  Telephone1: t('telephone1'),
  JobTitle: t('job_title'),
  Country: c('country'),
  Certification: t('certification'),
  AccessRoles: t('access_roles'),
  YearJoined: i('year_joined'),
  disassociated: b('disassociated'),
  active: b('active'),
  LastUpdated: d('last_updated'),
};

/** `OrgName`, `OrgWebAddress` y `PrimarySiteId` viajan en el registro de
 *  site, y `OrgName` / `SiteName` / `SiteCountry` en el de asignación, pero
 *  pertenecen a la organización o al site: la API los reconstruye con JOIN
 *  en lugar de duplicarlos. */
export const DERIVED_SITE_FIELDS = ['OrgName', 'OrgWebAddress', 'PrimarySiteId'] as const;
export const DERIVED_ASSIGNMENT_FIELDS = ['OrgId', 'OrgName', 'SiteName', 'SiteCountry'] as const;

/* =========================================================
   Qué campos se pueden editar ficha a ficha.

   La pantalla ya lo dice: ámbar y rojo se editan, verde y gris no. Pero
   eso vivía SOLO en el navegador. El servidor aceptaba cualquier columna
   conocida, así que una petición hecha a mano —no hace falta más que las
   herramientas del propio navegador— cambiaba el nombre de una
   organización, su país o su dirección de contrato, que son datos del
   contrato con ETC y no del socio.

   Esta lista es la que manda de verdad. Vive aquí, junto al mapa de
   columnas, porque el servidor no puede importar `src/lib/fields.ts`
   (arrastra React). `scripts/verificar-campos.mjs` comprueba que las dos
   digan lo mismo, y corre antes de cada publicación.

   Los datos maestros —nombre, país, ATCCSN, contrato— entran por la
   importación del Excel de ETC, que es su vía. Tampoco el Admin ETC los
   toca campo a campo: no es una restricción de permisos, es de origen.
   ========================================================= */

export const ORG_EDITABLE = new Set([
  'OrgWebAddress', 'YearJoined',
  'RegisteredAddress1', 'RegisteredAddress2', 'RegisteredAddress3',
  'RegisteredStateProvince', 'RegisteredPostalCode',
  'InvoicingAddress1', 'InvoicingAddress2', 'InvoicingAddress3',
  'InvoicingCity', 'InvoicingStateProvince', 'InvoicingCountry', 'InvoicingPostalCode',
  'BillingContactFirstName', 'BillingContactLastName',
  'BillingContactEmailAddress', 'BillingContactTelephone', 'BillingContactFax',
  'EmployeeCount', 'OrgIndustry', 'ProductPortfolio', 'ProductPortfolioOther',
  'TrainingEnvironment',
  'OffersCertPrep', 'OffersEduTraining', 'InterestedEduTraining',
]);

/** Los campos ROJOS de la organización.
 *
 *  A diferencia de los del site, estos NO impiden guardar. El socio puede
 *  corregir un teléfono sin haber contestado todavía cuántos empleados
 *  tiene; lo que pasa es que su porcentaje sigue con pendientes hasta que
 *  lo haga. Es una decisión de ETC: bloquear el guardado por un campo que
 *  nadie ha rellenado nunca dejaría a los 134 socios sin poder tocar nada.
 *
 *  `ProductPortfolioOther` no está: es el texto libre de «Other», y solo
 *  hace falta si esa opción está marcada. Su regla vive en el esquema. */
export const ORG_REQUIRED = new Set([
  'EmployeeCount', 'OrgIndustry', 'ProductPortfolio', 'TrainingEnvironment',
  'OffersCertPrep', 'OffersEduTraining', 'InterestedEduTraining',
]);

export const SITE_EDITABLE = new Set([
  'CommercialSiteName', 'Workstations',
  'SiteAddress1', 'SiteAddress2', 'SiteAddress3', 'SiteCity',
  'SiteStateProvince', 'SitePostalCode',
  'RegisteredPostalCode', 'RegisteredStateProvince',
  'SiteTelephone', 'SiteFax', 'SiteEmailAddress', 'SiteWebAddress',
  'ATCDirectorFirstName', 'ATCDirectorLastName', 'ATCDirectorEmail',
  'ATCDirectorTelephone', 'ATCDirectorFax',
  'SiteAdminFirstName', 'SiteAdminLastName', 'SiteAdminEmail',
  'SiteAdminTelephone', 'SiteAdminFax',
  'SiteManagerFirstName', 'SiteManagerLastName', 'SiteManagerEmail',
  'SiteManagerTelephone', 'SiteManagerFax',
]);

/** Los campos ROJOS del site: editables y además obligatorios. Son los que
 *  el recordatorio previo al cierre busca vacíos. Van aquí por el mismo
 *  motivo que la lista de editables — el servidor no puede leer fields.ts. */
export const SITE_REQUIRED = new Set([
  'CommercialSiteName', 'Workstations', 'RegisteredPostalCode',
]);

export const INSTRUCTOR_EDITABLE = new Set([
  'EnglishFirstName', 'EnglishLastName', 'EmailAddress', 'Mobile1', 'Telephone1',
]);

/* ---------------- «no aplica» ---------------- */

/** Las tres entidades que llevan porcentaje del proceso de actualización. */
export type EntidadNA = 'organization' | 'site' | 'instructor';

const EDITABLES_POR_ENTIDAD: Record<EntidadNA, Set<string>> = {
  organization: ORG_EDITABLE,
  site: SITE_EDITABLE,
  instructor: INSTRUCTOR_EDITABLE,
};

/** ¿Se puede marcar este campo como «no aplica»?
 *
 *  Solo los editables, y de esos nunca los rojos: un campo obligatorio que
 *  se puede excusar deja de ser obligatorio. La comprobación vive aquí
 *  porque quien la hace cumplir es el servidor — el cliente la usa para no
 *  ofrecer la casilla donde no toca, que es otra cosa. */
const OBLIGATORIOS_POR_ENTIDAD: Partial<Record<EntidadNA, Set<string>>> = {
  organization: ORG_REQUIRED,
  site: SITE_REQUIRED,
};

export function sePuedeExcusar(entidad: EntidadNA, campo: string): boolean {
  if (OBLIGATORIOS_POR_ENTIDAD[entidad]?.has(campo)) return false;
  return EDITABLES_POR_ENTIDAD[entidad].has(campo);
}

/** Valores que van siempre en un campo de lista y que el socio no elige.
 *
 *  No cuentan como respuesta. «Aprendizaje en el aula» lo tienen las 134
 *  organizaciones desde la migración porque todo ATC da clase presencial:
 *  si contara, el campo saldría resuelto sin que nadie hubiera contestado
 *  lo que ETC de verdad pregunta, que es qué OTRAS modalidades ofrece.
 *
 *  Vive aquí y no solo en el esquema de la pantalla porque lo necesitan los
 *  dos lados: el servidor para reponerlo en cada escritura, y el cálculo del
 *  porcentaje para descontarlo. */
export const FIJAS_POR_CAMPO: Record<string, string[]> = {
  TrainingEnvironment: ['classroom'],
};

/** Lo elegido de verdad en un campo de lista: lo guardado menos las fijas. */
export const sinFijas = (campo: string, valor: unknown): string[] => {
  const fijas = new Set(FIJAS_POR_CAMPO[campo] ?? []);
  return aLista(valor).filter((c) => !fijas.has(c));
};

/** Devuelve la lista con sus opciones fijas puestas.
 *
 *  El servidor la aplica en cada escritura y no se fía de que el navegador
 *  las mande: una petición hecha a mano, una importación de Excel o un
 *  cliente viejo podrían dejar la organización sin «Aprendizaje en el aula»,
 *  y ese valor tiene que estar en todas. */
export function conFijas(campo: string, valor: unknown): string[] {
  const lista = aLista(valor);
  for (const f of FIJAS_POR_CAMPO[campo] ?? []) {
    if (!lista.includes(f)) lista.push(f);
  }
  return lista;
}

/* ---------------- catálogos ---------------- */

/** Una opción de un desplegable o de un grupo de casillas.
 *
 *  El código es lo que se guarda; las etiquetas, lo que se enseña. Se separan
 *  porque el mismo dato lo escriben personas en dos idiomas: guardando la
 *  etiqueta, dos socios con la misma industria acabarían con valores
 *  distintos y no habría forma de contarlos juntos. */
export interface OpcionCatalogo {
  code: string;
  label_es: string;
  label_en: string;
}

/* Los catálogos viven aquí, en el archivo que comparten los dos lados, y no
   en `src/lib/fields.ts`. Los necesita la pantalla para pintarlos, pero
   también el servidor: para rechazar un código inventado y para traducir lo
   que venga en una celda de Excel, donde la gente escribe la etiqueta y no
   el código. Duplicarlos era garantizar que se separaran. */

const marca = (code: string, nombre: string): OpcionCatalogo =>
  ({ code, label_es: nombre, label_en: nombre });

/** Sí / No, para las tres preguntas del perfil.
 *
 *  Van como texto y no como booleano: un booleano no sabe decir «todavía no
 *  ha contestado». Sin responder valdría false, que se lee como «No», y el
 *  porcentaje daría el campo por resuelto sin que nadie hubiera contestado.
 *  Vacío es «sin respuesta» y es un estado distinto de «no». */
const SI_NO: OpcionCatalogo[] = [
  { code: 'si', label_es: 'Sí', label_en: 'Yes' },
  { code: 'no', label_es: 'No', label_en: 'No' },
];

export const CATALOGOS: Record<string, OpcionCatalogo[]> = {
  EmployeeCount: [
    { code: '0-10',    label_es: '0-10',       label_en: '0-10' },
    { code: '11-50',   label_es: '11-50',      label_en: '11-50' },
    { code: '51-100',  label_es: '51-100',     label_en: '51-100' },
    { code: '101-500', label_es: '101-500',    label_en: '101-500' },
    { code: '>500',    label_es: 'Más de 500', label_en: 'More than 500' },
  ],
  OrgIndustry: [
    { code: 'dm',   label_es: 'Diseño y fabricación (D&M)',                     label_en: 'Design and manufacturing (D&M)' },
    { code: 'aeco', label_es: 'Arquitectura, ingeniería y construcción (AECO)', label_en: 'Architecture, Engineering & Construction (AECO)' },
    { code: 'me',   label_es: 'Medios y entretenimiento (M&E)',                 label_en: 'Media & Entertainment (M&E)' },
  ],
  // nombres propios: no se traducen. «Blender» se llama Blender en las dos
  // lenguas, y las dos etiquetas se repiten a propósito.
  ProductPortfolio: [
    marca('adobe', 'Adobe'),
    marca('archicad', 'ArchiCAD'),
    marca('bricscad', 'BricsCAD'),
    marca('bentley', 'Bentley'),
    marca('blender', 'Blender'),
    marca('catia', 'CATIA'),
    marca('draftsight', 'DraftSight'),
    marca('onshape', 'Onshape'),
    // Los nombres van como los escribe el fabricante. Llegaron sin el
    // espacio de la fuente original, y «SiemensNX» no es como se llama el
    // producto. Solo cambia la etiqueta: el código guardado sigue siendo el
    // mismo, así que lo que ya está en la base no se toca.
    marca('ptccreo', 'PTC Creo'),
    marca('siemensnx', 'Siemens NX/SolidEdge'),
    marca('solidworks', 'SolidWorks'),
    marca('zwcad', 'ZWCAD'),
    { code: 'other', label_es: 'Otro',    label_en: 'Other' },
    { code: 'none',  label_es: 'Ninguno', label_en: 'None' },
  ],
  TrainingEnvironment: [
    { code: 'online',    label_es: 'Aprendizaje en línea',    label_en: 'Online Learning' },
    { code: 'elearning', label_es: 'Aprendizaje electrónico', label_en: 'E-Learning' },
    { code: 'hybrid',    label_es: 'Aprendizaje híbrido',     label_en: 'Hybrid Learning' },
    { code: 'classroom', label_es: 'Aprendizaje en el aula',  label_en: 'Classroom Learning' },
  ],
  OffersCertPrep: SI_NO,
  OffersEduTraining: SI_NO,
  InterestedEduTraining: SI_NO,
};

const sinAcentos = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();

/** Además sin espacios.
 *
 *  «SiemensNX/SolidEdge» y «Siemens NX/SolidEdge» son la misma respuesta. La
 *  etiqueta se corrigió para que llevara el espacio que el producto tiene de
 *  verdad, y sin esto una plantilla rellenada con la grafía anterior dejaría
 *  de importar de un día para otro. */
const sinEspacios = (s: string) => sinAcentos(s).replace(/\s+/g, '');

/** Traduce lo que venga a códigos del catálogo.
 *
 *  Acepta el código, la etiqueta en castellano y la etiqueta en inglés, sin
 *  distinguir mayúsculas ni acentos. Es lo que permite que un informe
 *  exportado se vuelva a importar sin tocarlo, y que quien rellena la
 *  plantilla escriba «AECO» y no `aeco`.
 *
 *  Lo que no reconoce lo devuelve tal cual, para que `codigosDesconocidos`
 *  lo señale con el valor que escribió la persona y no con uno normalizado
 *  que no reconocería en su archivo. */
export function aCodigos(campo: string, valor: unknown): string[] {
  const cat = CATALOGOS[campo];
  if (!cat) return aLista(valor);
  const indice = new Map<string, string>();
  const laxo = new Map<string, string>();
  for (const o of cat) {
    for (const forma of [o.code, o.label_es, o.label_en]) {
      indice.set(sinAcentos(forma), o.code);
      // el segundo índice no pisa al primero: solo se consulta si el exacto
      // no encontró nada
      if (!laxo.has(sinEspacios(forma))) laxo.set(sinEspacios(forma), o.code);
    }
  }
  return aLista(valor).map((v) => indice.get(sinAcentos(v)) ?? laxo.get(sinEspacios(v)) ?? v);
}

/** Los valores de `campo` que no están en su catálogo.
 *  Vacío si el campo no tiene catálogo o si todos son válidos. */
export function codigosDesconocidos(campo: string, valor: unknown): string[] {
  const cat = CATALOGOS[campo];
  if (!cat) return [];
  const validos = new Set(cat.map((o) => o.code));
  return aLista(valor).filter((c) => !validos.has(c));
}

/** Campos que solo cuentan si otro campo lleva cierto valor.
 *
 *  «Otro producto, ¿cuál?» únicamente tiene sentido si el socio marcó
 *  «Otro» en el portafolio. Contándolo siempre, quien no lo marque se
 *  quedaría a un campo del 100 % para siempre, y tendría que excusar a mano
 *  algo que por construcción no le aplica. */
export const SOLO_SI: Record<string, { campo: string; code: string }> = {
  ProductPortfolioOther: { campo: 'ProductPortfolio', code: 'other' },
};

/** ¿Este campo cuenta en ESTA ficha?
 *
 *  Distinto de estar en la lista de los que cuentan: aquella es del tipo de
 *  entidad, esta mira los valores concretos del registro. */
export function cuentaEnEstaFicha(campo: string, record: Record<string, unknown>): boolean {
  const cond = SOLO_SI[campo];
  return !cond || aLista(record[cond.campo]).includes(cond.code);
}

/** Los campos de una entidad que cuentan para su porcentaje.
 *
 *  Son los editables enteros, rojos incluidos: un rojo vacío tiene que
 *  restar. Lo que sale del denominador son las marcas «no aplica», y eso lo
 *  decide quien calcula, no esta lista. */
export const camposQueCuentan = (entidad: EntidadNA): string[] =>
  [...EDITABLES_POR_ENTIDAD[entidad]];

/** Un valor que no se puede convertir al tipo de su columna.
 *  Se distingue de cualquier otro fallo para poder decirle a quien edita
 *  qué campo corregir, en vez de un error genérico. */
export class ValorInvalido extends Error {
  constructor(readonly field: string, readonly kind: ColumnKind, readonly raw: string) {
    super(`El campo ${field} no admite el valor "${raw}"`);
    this.name = 'ValorInvalido';
  }
}

/** Convierte un valor de la aplicación al tipo que espera la columna.
 *
 *  Vacío significa «sin dato» y se guarda como NULL: es una forma legítima
 *  de limpiar un campo. Pero un valor NO vacío que no se puede convertir
 *  —texto en un campo numérico, una fecha mal escrita— es un error y se
 *  rechaza. Antes se convertía en NULL en silencio, así que un dedazo
 *  borraba el dato que había y la aplicación decía que había guardado bien. */
export function toColumnValue(
  kind: ColumnKind,
  raw: unknown,
  field = '(desconocido)',
): string | number | boolean | string[] | null {
  if (kind === 'bool') return raw === true || raw === 'true';
  // Una lista vacía es un dato legítimo —«todavía no ha contestado»— y se
  // guarda como '{}', no como NULL: la columna es NOT NULL DEFAULT '{}' y
  // así el porcentaje no tiene que distinguir dos formas de estar vacío.
  if (kind === 'list') return aLista(raw);
  const s = raw == null ? '' : String(raw).trim();
  if (kind === 'text') return s;
  if (s === '') return null;
  if (kind === 'country') return s.toUpperCase();
  if (kind === 'int') {
    // parseInt aceptaría "12abc"; aquí tiene que ser un entero y nada más
    if (!/^-?\d+$/.test(s)) throw new ValorInvalido(field, kind, s);
    const n = Number.parseInt(s, 10);
    if (!Number.isSafeInteger(n)) throw new ValorInvalido(field, kind, s);
    return n;
  }
  // date: se acepta únicamente yyyy-mm-dd, que es lo que produce la app
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) throw new ValorInvalido(field, kind, s);
  return s;
}

/** Fila lista para INSERT: columnas, marcadores y valores. */
export function buildRow(map: ColumnMap, record: Record<string, unknown>) {
  const cols: string[] = [];
  const values: (string | number | boolean | string[] | null)[] = [];
  for (const [field, { col, kind }] of Object.entries(map)) {
    if (!(field in record)) continue;
    cols.push(col);
    values.push(toColumnValue(kind, record[field]));
  }
  return { cols, values, params: cols.map((_, n) => `$${n + 1}`) };
}
